import { FC, Suspense, useCallback, useState } from 'react';

import { graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';
import { formatDistanceToNow, parseISO } from 'date-fns';

import {
  DebouncedInput,
  DefaultListLayoutItemHeader,
  DefaultListLayoutSectionElement,
  ListLayout,
  Loading,
  SectionData,
} from 'shared-ui';
import { isNotNullOrUndefined, useDebounce } from 'shared-lib';

import {
  IdentityUserFilter,
  IdentityUserListContainerQuery,
} from './__generated__/IdentityUserListContainerQuery.graphql';
import { IdentityUserListContainer_list$key } from './__generated__/IdentityUserListContainer_list.graphql';

const queryDef = graphql`
  query IdentityUserListContainerQuery($where: IdentityUserFilter!) {
    ...IdentityUserListContainer_list
  }
`;

const fragmentDef = graphql`
  fragment IdentityUserListContainer_list on Query
  @argumentDefinitions(
    first: { type: Int, defaultValue: 10 }
    after: { type: String }
  )
  @refetchable(queryName: "IdentityUsersListPaginationQuery") {
    list: identityUsers(
      first: $first
      after: $after
      order: [{ firstName: DESC }]
      where: $where
    ) @connection(key: "IdentityUserListContainer_list") {
      __id
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      edges {
        cursor
        node {
          id
          code
          identityUserId
          lastLoggedIn
          updatedAt
          createdAt
          firstName
          lastName
          email
          accountTypes
        }
      }
    }
  }
`;

function createLinkTo(section: SectionData): string {
  const code = section.context.code;
  return `../../../view/${code}`;
}

type ListProps = {
  fragmentRef: IdentityUserListContainer_list$key;
};

const AdminUserList: FC<ListProps> = ({ fragmentRef }) => {
  const { data, loadNext, hasNext, isLoadingNext, refetch } =
    usePaginationFragment<
      IdentityUserListContainerQuery,
      IdentityUserListContainer_list$key
    >(fragmentDef, fragmentRef);

  const onLoadNext = useCallback(() => {
    loadNext(10);
  }, [loadNext]);

  const { list } = data;
  const sections: Array<SectionData> = (list?.edges || [])
    .map(e => e.node)
    .filter(isNotNullOrUndefined)
    .map(user => {
      const name = `${user.email}`;

      return {
        key: user.code.toString(),
        itemTitle: name,
        listTitle: (
          <div className='flex flex-col gap-2'>
            <div className='flex items-center justify-between gap-2'>
              <div className='text-md font-medium uppercase'>{user.email}</div>
              <div className='text-md text-blue-500'>{user.code}</div>
            </div>
          </div>
        ),
        description: (
          <div className='pt-2 text-sm'>
            Updated {formatDistanceToNow(parseISO(user.updatedAt))}
          </div>
        ),
        hideIcon: true,
        icon: () => null,
        iconForeground: 'text-primary-700',
        iconBackground: 'bg-primary-50',
        context: {
          code: user.code,
        },
        element: (
          <DefaultListLayoutSectionElement>
            <div className='grid'>
              <div>{user.code}</div>
            </div>
          </DefaultListLayoutSectionElement>
        ),
      };
    });

  return (
    <ListLayout
      title='Identity Users'
      sections={sections}
      itemHeader={s => (
        <DefaultListLayoutItemHeader
          title='Identity Users'
          to={createLinkTo}
          section={s}
        />
      )}
      loadMore={onLoadNext}
      isLoading={false}
      showFilters={true}
    />
  );
};

export const IdentityUserListContainer: FC = () => {
  const [filter, setFilter] = useState<string | null>(null);

  const searchTerm = useDebounce(filter, 500);

  let where: IdentityUserFilter = {};

  if (searchTerm) {
    where = {
      or: [{ email: { startsWith: filter } }],
    };
  }

  const query = useLazyLoadQuery<IdentityUserListContainerQuery>(
    queryDef,
    { where },
    {
      fetchPolicy: 'store-and-network',
    },
  );

  return (
    <div className='flex flex-col'>
      <div className='flex items-center justify-between space-x-4 border-b bg-white'>
        <div className='flex items-center space-x-4 p-2'>
          <label className='text-gray-700' htmlFor='filter'>
            Filter
          </label>
          <div className='sm:w-64'>
            <DebouncedInput
              value={undefined}
              placeholder={'Name'}
              onValueChanged={setFilter}
            />
          </div>
        </div>
      </div>
      <div className='flex-1'>
        <Suspense fallback={<Loading />}>
          <AdminUserList fragmentRef={query} />
        </Suspense>
      </div>
    </div>
  );
};
